import React, { useState, useEffect } from 'react';
import { usersApi } from '../../services/api';
import DeleteReasonModal from '../shared/DeleteReasonModal';
import SuspendUserModal from '../shared/SuspendUserModal';

// List of known universities for autocomplete
const UNIVERSITIES = [
  'Uni Hamburg',
  'TU Hamburg',
  'HafenCity Universität',
  'HAW Hamburg',
  'Bucerius Law School',
  'HfBK Hamburg',
  'Helmut-Schmidt-Universität',
  'Kühne Logistics University',
  'Hochschule für Musik und Theater',
  'Muthesius Kunsthochschule',
  'Christian-Albrechts-Universität Kiel',
  'HAW Kiel',
  'TH Lübeck',
  'Hochschule Flensburg',
  'Musikhochschule Lübeck',
  'HTW Berlin',
  'TU Berlin',
  'LMU Munich',
  'TUM',
  'RWTH Aachen',
  'University of Heidelberg',
  'University of Bonn',
  'University of Munich',
];

function UserList({ onUserSelect }) {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Issue #202: Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteReasonError, setDeleteReasonError] = useState('');

  // Issue #417: Suspend/Unsuspend modal state
  const [suspendTarget, setSuspendTarget] = useState(null); // { id, name }
  const [unsuspendTarget, setUnsuspendTarget] = useState(null); // { id, name }

  // Filter state
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const [filters, setFilters] = useState({
    enrolledUniversity: '',
    lastLoginFrom: '',
    lastLoginTo: '',
    status: ''
  });
  const [tempFilters, setTempFilters] = useState({ ...filters });

  // University autocomplete state
  const [showUniversitySuggestions, setShowUniversitySuggestions] = useState(false);
  const [universitySuggestions, setUniversitySuggestions] = useState([]);

  const perPage = 20;

  // Count active filters
  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  const fetchUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = { page, per_page: perPage };
      if (search) params.search = search;
      if (filters.enrolledUniversity) params.enrolled_university = filters.enrolledUniversity;
      if (filters.lastLoginFrom) params.last_login_from = filters.lastLoginFrom;
      if (filters.lastLoginTo) params.last_login_to = filters.lastLoginTo;
      if (filters.status) params.status = filters.status;

      const response = await usersApi.list(params);
      setUsers(response.data.users);
      setTotal(response.data.total);
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
      console.error('Users error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, filters]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleOpenFilterPopover = () => {
    setTempFilters({ ...filters });
    setShowFilterPopover(true);
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    setShowFilterPopover(false);
  };

  const handleCancelFilters = () => {
    setTempFilters({ ...filters });
    setShowFilterPopover(false);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      enrolledUniversity: '',
      lastLoginFrom: '',
      lastLoginTo: '',
      status: ''
    };
    setFilters(emptyFilters);
    setTempFilters(emptyFilters);
    setPage(1);
  };

  // Handle university input change with autocomplete
  const handleUniversityInputChange = (value) => {
    setTempFilters({ ...tempFilters, enrolledUniversity: value });
    if (value.length > 0) {
      const filtered = UNIVERSITIES.filter(uni =>
        uni.toLowerCase().includes(value.toLowerCase())
      );
      setUniversitySuggestions(filtered);
      setShowUniversitySuggestions(filtered.length > 0);
    } else {
      setUniversitySuggestions([]);
      setShowUniversitySuggestions(false);
    }
  };

  const handleUniversitySelect = (university) => {
    setTempFilters({ ...tempFilters, enrolledUniversity: university });
    setShowUniversitySuggestions(false);
  };

  const handleAction = async (action, userId) => {
    setActionLoading(true);
    try {
      switch (action) {
        case 'activate':
          await usersApi.activate(userId);
          break;
        case 'deactivate':
          await usersApi.deactivate(userId);
          break;
        case 'suspend':
        case 'unsuspend':
        case 'delete':
          // Issue #202 / #417: Handled by modals, not here.
          break;
        case 'resetPassword':
          await usersApi.resetPassword(userId);
          alert('Passwort-Reset wurde initiiert');
          break;
        case 'resendVerification':
          await usersApi.resendVerification(userId);
          alert('Verifizierungs-E-Mail wurde erneut gesendet');
          break;
        default:
          break;
      }
      fetchUsers();
    } catch (err) {
      alert('Aktion fehlgeschlagen: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Issue #417: Confirm suspension with days + reason (>=20 chars)
  const handleSuspendConfirm = async (days, reason) => {
    await usersApi.suspend(suspendTarget.id, days, reason);
    setSuspendTarget(null);
    fetchUsers();
  };

  // Issue #417: Confirm unsuspension with reason (>=20 chars)
  const handleUnsuspendConfirm = async (reason) => {
    await usersApi.unsuspend(unsuspendTarget.id, reason);
    setUnsuspendTarget(null);
    fetchUsers();
  };

  // Issue #202: Handle delete confirmation with reason
  const handleDeleteConfirm = async () => {
    // Validate reason length
    const trimmedReason = deleteReason.trim();
    if (trimmedReason.length < 20) {
      setDeleteReasonError('Der Grund muss mindestens 20 Zeichen lang sein');
      return;
    }

    setActionLoading(true);
    setDeleteReasonError('');
    try {
      await usersApi.delete(deleteUserId, trimmedReason);
      setShowDeleteModal(false);
      setDeleteUserId(null);
      setDeleteUserName('');
      setDeleteReason('');
      fetchUsers();
    } catch (err) {
      setDeleteReasonError('Löschen fehlgeschlagen: ' + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isUserSuspended = (user) => {
    if (!user.suspended_until) return false;
    return new Date(user.suspended_until) > new Date();
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Benutzerkontoverwaltung</h1>
        <p className="page-subtitle">{total} Benutzer insgesamt</p>
      </div>

      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name oder E-Mail suchen..."
            className="search-input"
          />
          <button type="submit" className="search-button">Suchen</button>
        </form>

        <div className="filter-controls">
          <button
            type="button"
            className="filter-button"
            onClick={handleOpenFilterPopover}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
            </svg>
            Filter
            {activeFilterCount > 0 && (
              <span className="filter-count-badge">{activeFilterCount}</span>
            )}
          </button>

          {activeFilterCount > 0 && (
            <button
              type="button"
              className="reset-filter-button"
              onClick={handleResetFilters}
            >
              Filter zurücksetzen
            </button>
          )}
        </div>
      </div>

      {/* Filter Popover */}
      {showFilterPopover && (
        <div className="filter-popover-overlay" onClick={handleCancelFilters}>
          <div className="filter-popover" onClick={(e) => e.stopPropagation()}>
            <div className="filter-popover-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/>
              </svg>
              <span className="filter-title">Filter</span>
            </div>

            <div className="filter-popover-content">
              {/* Enrolled University */}
              <div className="filter-section">
                <label className="filter-section-label">Hochschule</label>
                <div className="autocomplete-container">
                  <input
                    type="text"
                    className="filter-input"
                    placeholder="z.B. TU Hamburg, HTW Berlin..."
                    value={tempFilters.enrolledUniversity}
                    onChange={(e) => handleUniversityInputChange(e.target.value)}
                    onFocus={() => {
                      if (tempFilters.enrolledUniversity.length > 0) {
                        const filtered = UNIVERSITIES.filter(uni =>
                          uni.toLowerCase().includes(tempFilters.enrolledUniversity.toLowerCase())
                        );
                        setUniversitySuggestions(filtered);
                        setShowUniversitySuggestions(filtered.length > 0);
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowUniversitySuggestions(false), 200);
                    }}
                  />
                  {showUniversitySuggestions && universitySuggestions.length > 0 && (
                    <ul className="autocomplete-suggestions">
                      {universitySuggestions.map((uni, index) => (
                        <li
                          key={index}
                          className="autocomplete-suggestion"
                          onClick={() => handleUniversitySelect(uni)}
                        >
                          {uni}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Last Login Date Range */}
              <div className="filter-section">
                <label className="filter-section-label">Letzter Login</label>
                <div className="date-range-inputs">
                  <div className="date-input-group">
                    <label className="date-label">Von</label>
                    <input
                      type="datetime-local"
                      className="filter-input date-input"
                      value={tempFilters.lastLoginFrom}
                      onChange={(e) => setTempFilters({ ...tempFilters, lastLoginFrom: e.target.value })}
                    />
                  </div>
                  <div className="date-input-group">
                    <label className="date-label">Bis</label>
                    <input
                      type="datetime-local"
                      className="filter-input date-input"
                      value={tempFilters.lastLoginTo}
                      onChange={(e) => setTempFilters({ ...tempFilters, lastLoginTo: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Account Status */}
              <div className="filter-section">
                <label className="filter-section-label">Kontostatus</label>
                <select
                  className="filter-input"
                  value={tempFilters.status}
                  onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })}
                >
                  <option value="">Alle Status</option>
                  <option value="verified">Verifiziert</option>
                  <option value="unverified">Nicht verifiziert</option>
                  <option value="suspended">Gesperrt</option>
                  <option value="deletion_requested">Löschung angefordert</option>
                </select>
              </div>

              {/* Buttons */}
              <div className="filter-popover-buttons">
                <button
                  type="button"
                  className="filter-cancel-button"
                  onClick={handleCancelFilters}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="filter-apply-button"
                  onClick={handleApplyFilters}
                >
                  Anwenden
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>E-Mail</th>
                  <th>Hochschule</th>
                  <th>Status</th>
                  <th>Registriert</th>
                  <th>Letzter Login</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-table-message">
                      {activeFilterCount > 0 ? 'Keine Benutzer gefunden mit den gewählten Filtern' : 'Keine Benutzer vorhanden'}
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr
                      key={user.id}
                      className={`clickable-row ${isUserSuspended(user) ? 'suspended-row' : ''} ${user.deletion_requested_at ? 'deletion-requested-row' : ''}`}
                      onClick={() => onUserSelect && onUserSelect(user.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{user.first_name} {user.last_name}</td>
                      <td>{user.email}</td>
                      <td>{user.enrolled_university || '-'}</td>
                      <td>
                        {user.deletion_requested_at ? (
                          <span className="status-badge deletion-requested">Löschung angefordert</span>
                        ) : !user.email_verified ? (
                          <span className="status-badge unverified">Nicht verifiziert</span>
                        ) : isUserSuspended(user) ? (
                          <span className="status-badge suspended">Gesperrt</span>
                        ) : (
                          <span className="status-badge verified">Verifiziert</span>
                        )}
                      </td>
                      <td>{formatDate(user.created_at)}</td>
                      <td>{formatDate(user.last_login)}</td>
                      <td className="actions-cell" onClick={(e) => e.stopPropagation()}>
                        <div className="action-buttons">
                          {!user.email_verified ? (
                            <>
                              <button
                                onClick={() => handleAction('activate', user.id)}
                                disabled={actionLoading}
                                className="action-btn activate"
                                title="Aktivieren"
                              >
                                ✓
                              </button>
                              <button
                                onClick={() => handleAction('resendVerification', user.id)}
                                disabled={actionLoading}
                                className="action-btn resend"
                                title="Verifizierung erneut senden"
                              >
                                📧
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleAction('deactivate', user.id)}
                              disabled={actionLoading}
                              className="action-btn deactivate"
                              title="Deaktivieren"
                            >
                              ✗
                            </button>
                          )}
                          {isUserSuspended(user) ? (
                            <button
                              onClick={() => setUnsuspendTarget({
                                id: user.id,
                                name: `${user.first_name} ${user.last_name}`,
                              })}
                              disabled={actionLoading}
                              className="action-btn unsuspend"
                              title="Sperre aufheben"
                            >
                              🔓
                            </button>
                          ) : (
                            <button
                              onClick={() => setSuspendTarget({
                                id: user.id,
                                name: `${user.first_name} ${user.last_name}`,
                              })}
                              disabled={actionLoading}
                              className="action-btn suspend"
                              title="Sperren"
                            >
                              🔒
                            </button>
                          )}
                          <button
                            onClick={() => handleAction('resetPassword', user.id)}
                            disabled={actionLoading}
                            className="action-btn reset"
                            title="Passwort zurücksetzen"
                          >
                            🔑
                          </button>
                          <button
                            onClick={() => {
                              setDeleteUserId(user.id);
                              setDeleteUserName(`${user.first_name} ${user.last_name}`);
                              setDeleteReason('');
                              setDeleteReasonError('');
                              setShowDeleteModal(true);
                            }}
                            disabled={actionLoading}
                            className="action-btn delete"
                            title="Löschen"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="pagination-btn"
              >
                ← Zurück
              </button>
              <span className="pagination-info">
                Seite {page} von {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="pagination-btn"
              >
                Weiter →
              </button>
            </div>
          )}
        </>
      )}

      {/* Issue #417: Suspend Confirmation Modal */}
      <SuspendUserModal
        open={suspendTarget !== null}
        userName={suspendTarget?.name}
        onConfirm={handleSuspendConfirm}
        onCancel={() => setSuspendTarget(null)}
      />

      {/* Issue #417: Unsuspend Confirmation Modal */}
      <DeleteReasonModal
        open={unsuspendTarget !== null}
        title="Sperre aufheben"
        description={unsuspendTarget
          ? `Sie sind dabei, die Sperre des Kontos von ${unsuspendTarget.name} aufzuheben.`
          : undefined}
        prompt="Bitte geben Sie einen Grund für das Entsperren an (mindestens 20 Zeichen):"
        placeholder="Grund für das Entsperren eingeben..."
        confirmLabel="Entsperren"
        failureFallback="Entsperren fehlgeschlagen"
        onConfirm={handleUnsuspendConfirm}
        onCancel={() => setUnsuspendTarget(null)}
      />

      {/* Issue #202: Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Benutzer löschen</h2>
            <p>
              Sie sind dabei, den Benutzer <strong>{deleteUserName}</strong> zu löschen.
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <p>Bitte geben Sie einen Grund für die Löschung an (mindestens 20 Zeichen):</p>
            <div className="form-group">
              <textarea
                value={deleteReason}
                onChange={(e) => {
                  setDeleteReason(e.target.value);
                  if (deleteReasonError) setDeleteReasonError('');
                }}
                placeholder="Grund für die Löschung eingeben..."
                className="filter-input delete-reason-input"
                rows={4}
              />
              <div className="character-count">
                {deleteReason.trim().length}/20 Zeichen
              </div>
              {deleteReasonError && (
                <div className="error-text">{deleteReasonError}</div>
              )}
            </div>
            <div className="form-actions">
              <button
                className="secondary-button"
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteUserId(null);
                  setDeleteUserName('');
                  setDeleteReason('');
                  setDeleteReasonError('');
                }}
              >
                Abbrechen
              </button>
              <button
                className="primary-button delete-confirm-button"
                onClick={handleDeleteConfirm}
                disabled={actionLoading || deleteReason.trim().length < 20}
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserList;
